import { useState } from "react";
import { Card, CardContent } from "../ui/card";
import { TrendingUp } from "lucide-react";
import { groupForSource, SOURCE_GROUPS } from "../../lib/leadSources";

/**
 * Compact tile showing lead counts grouped by source, with a 7d / 30d toggle.
 * Renders all groups (even zero counts) so the layout doesn't jump around.
 */
export function LeadSourceTile({ contacts = [] }) {
  const [windowDays, setWindowDays] = useState(7);

  const now = new Date();
  const cutoffMs = now.getTime() - windowDays * 24 * 60 * 60 * 1000;

  // Initialise counts so zero buckets still render.
  const counts = Object.fromEntries(SOURCE_GROUPS.map((g) => [g, 0]));
  let total = 0;

  for (const c of contacts) {
    const ts = c.created_at ? new Date(c.created_at).getTime() : 0;
    if (!ts || ts < cutoffMs) continue;
    const group = groupForSource(c.source);
    counts[group] = (counts[group] ?? 0) + 1;
    total += 1;
  }

  const topGroup = Object.entries(counts).reduce(
    (best, [g, n]) => (n > best.count ? { group: g, count: n } : best),
    { group: null, count: 0 },
  );

  const RANGE_OPTIONS = [
    { value: 7, label: "7 days" },
    { value: 30, label: "30 days" },
  ];

  return (
    <Card className="border-slate-200" data-testid="leads-by-source-tile">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-5 h-5 text-gold" />
            </div>
            <div>
              <div className="text-sm text-slate-500 leading-tight">
                Leads · last {windowDays} days
              </div>
              <div className="font-heading font-bold text-navy text-2xl leading-tight" data-testid="leads-week-total">
                {total}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Range toggle */}
            <div
              className="inline-flex rounded-full bg-slate-100 p-1"
              role="tablist"
              data-testid="leads-range-toggle"
            >
              {RANGE_OPTIONS.map((opt) => {
                const active = windowDays === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setWindowDays(opt.value)}
                    data-testid={`leads-range-${opt.value}d`}
                    className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                      active
                        ? "bg-navy text-white shadow-sm"
                        : "text-slate-600 hover:text-navy"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            {total > 0 && topGroup.group && (
              <div className="text-right">
                <div className="text-xs text-slate-500">Top source</div>
                <div className="text-sm font-medium text-navy">{topGroup.group}</div>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-2">
          {SOURCE_GROUPS.map((group) => {
            const count = counts[group] || 0;
            const pct = total > 0 ? Math.round((count / total) * 100) : 0;
            return (
              <div
                key={group}
                className="flex items-center gap-3 text-sm"
                data-testid={`leads-source-row-${group.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="w-32 text-slate-600 flex-shrink-0">{group}</div>
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-navy rounded-full transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="w-8 text-right font-medium text-navy tabular-nums">{count}</div>
              </div>
            );
          })}
        </div>

        {total === 0 && (
          <p className="text-xs text-slate-400 mt-4">
            No leads in the selected window yet — CTA performance will show here once enquiries start coming in.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default LeadSourceTile;
